<template>
  <div
    class="delivery-page"
    v-loading="pageLoading"
    element-loading-text="数据处理中......"
  >
    <div class="page-header">
      <div class="page-header__left">
        <h2 class="page-header__title">{{ pageTitle }}</h2>
      </div>
      <div class="page-header__actions">
        <el-button size="small" plain @click="backHome">返回</el-button>
        <el-button
          v-if="showDraftButton"
          size="small"
          plain
          :loading="draftLoading"
          @click="handleSaveDraft"
        >暂存草稿</el-button>
        <el-button
          type="primary"
          size="small"
          :loading="submitLoading"
          @click="handleSubmit"
        >{{ submitButtonLabel }}</el-button>
        <el-button
          v-if="showAuditButton"
          type="success"
          size="small"
          plain
          :loading="auditLoading"
          @click="handleAudit(1)"
        >审核</el-button>
      </div>
    </div>

    <div class="delivery-card">
      <div class="delivery-card__head">
        <div class="delivery-card__head-left">
          <span class="delivery-card__label">发货单</span>
          <span class="delivery-card__title">{{ cardTitle }}</span>
        </div>
      </div>
      <el-form
        :model="form"
        label-position="top"
        size="small"
        class="delivery-form"
      >
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="工序委外订单号" required>
              <el-select
                v-model="form.baseProcessOutsourcingDocId"
                filterable
                clearable
                placeholder="请选择工序委外订单"
                style="width: 100%"
                :disabled="docIdExists"
                popper-class="process-order-select-dropdown"
                @change="handleOrderChange"
              >
                <el-option
                  v-for="item in orderOptions"
                  :key="item.processOutsourcingDocId || item.baseProcessOutsourcingDocId"
                  :label="formatProcessOrderLabel(item)"
                  :value="item.processOutsourcingDocId || item.baseProcessOutsourcingDocId"
                >
                  <div class="process-order-option">
                    <div class="process-order-option__title">
                      {{
                        item.processOutsourcingDocNumber ||
                        item.baseProcessOutsourcingDocNumber ||
                        "--"
                      }}
                    </div>
                    <div
                      v-if="formatProcessOrderOptionDesc(item)"
                      class="process-order-option__desc"
                    >
                      {{ formatProcessOrderOptionDesc(item) }}
                    </div>
                  </div>
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="预计到货日期">
              <el-date-picker
                v-model="form.baseExpectedArrivalDate"
                type="date"
                value-format="yyyy-MM-dd"
                format="yyyy/MM/dd"
                placeholder="请选择预计到货日期"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="发货单号">
              <el-input
                v-model="form.processCoopDeliveryNumber"
                placeholder="系统自动生成或手工填写"
                clearable
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="物流公司">
              <el-select
                v-model="form.baseLogisticsCompany"
                filterable
                allow-create
                default-first-option
                placeholder="请选择或输入物流公司"
                style="width: 100%"
              >
                <el-option
                  v-for="item in logisticsCompanyOptions"
                  :key="item"
                  :label="item"
                  :value="item"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="物流单号">
              <el-input
                v-model="form.baseLogisticsNumber"
                placeholder="请输入物流单号"
                clearable
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="联系方式">
              <el-input
                v-model="form.baseLogisticsContact"
                placeholder="请输入联系方式"
                clearable
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="备注">
              <el-input
                v-model="form.remark"
                type="textarea"
                :rows="2"
                placeholder="请输入备注"
                maxlength="512"
                show-word-limit
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </div>

    <div class="delivery-card delivery-card--detail">
      <div class="delivery-card__head delivery-card__head--plain">
        <span class="delivery-card__title">发货明细</span>
      </div>
      <el-table
        :data="detailTableData"
        border
        size="small"
        class="detail-table"
      >
        <el-table-column type="index" label="序号" width="55" align="center" />
        <el-table-column label="产品编号" min-width="120" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ row.baseMaterialNumber || "--" }}
          </template>
        </el-table-column>
        <el-table-column label="产品名称" min-width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ row.baseMaterialName || "--" }}
          </template>
        </el-table-column>
        <el-table-column label="规格" min-width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">
            {{ formatSpecLabel(row) }}
          </template>
        </el-table-column>
        <el-table-column label="本次出货" min-width="120">
          <template>
            <el-input-number
              v-model="form.baseOutsourcingFinishedQuantity"
              :min="0"
              :controls="false"
              size="small"
              class="detail-qty-input"
            />
          </template>
        </el-table-column>
        <el-table-column label="单位" min-width="70">
          <template slot-scope="{ row }">
            {{ row.baseMaterialUnitName || "--" }}
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import {
  processOutsourcing_list,
  processCoopDelivery_add,
  processCoopDelivery_saveDraft,
  processCoopDelivery_submitDraft,
  processCoopDelivery_auditByGlobal,
  processOutsourcingReceive_processOutsourcingCanReceiveProduction,
} from "@/api/myController/outsourcing";
import {
  LOGISTICS_COMPANIES,
  PROCESS_COOP_DOC_STATUS,
  createProcessDeliveryForm,
  hasDocId,
  formatProcessOrderLabel,
  formatProcessOrderOptionDesc,
  formatSpecLabel,
  pickProcessMaterialFields,
  normalizeProcessOrderSource,
  resolveProcessDeliveryQuantity,
  applyProcessDeliveryFields,
  pickProcessOrderFields,
  validateProcessDeliveryForm,
  buildProcessDeliveryPayload,
  buildProcessDraftActionPayload,
  buildProcessAuditPayload,
  resolveProcessAuditGlobalId,
  getProcessShipBlockMessage,
} from "@/utils/outsourcingProcessFinishDelivery";

export default {
  name: "OutsourcingProcessesFinishAdd",
  props: {
    routeType: {
      type: [Number, String],
      default: 0,
    },
    routeQuery: {
      default: () => ({}),
    },
    finish_type: {
      type: Number,
      default: 0,
    },
    itemList: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      pageLoading: false,
      draftLoading: false,
      submitLoading: false,
      auditLoading: false,
      orderOptions: [],
      logisticsCompanyOptions: LOGISTICS_COMPANIES,
      form: createProcessDeliveryForm(),
    };
  },
  computed: {
    docIdExists() {
      return hasDocId(this.form.processCoopDeliveryId);
    },
    isCooperativeDraft() {
      return (
        Number(this.form.statusCooperativeDoc) === PROCESS_COOP_DOC_STATUS.DRAFT
      );
    },
    showDraftButton() {
      return !this.docIdExists || this.isCooperativeDraft;
    },
    submitButtonLabel() {
      if (this.docIdExists && !this.isCooperativeDraft) {
        return "保存";
      }
      return "提交";
    },
    showAuditButton() {
      return (
        this.docIdExists &&
        !this.isCooperativeDraft &&
        Number(this.form.statusCheck) === 0
      );
    },
    pageTitle() {
      return this.finish_type === 1 ? "编辑工序委外发货单" : "新增工序委外发货单";
    },
    cardTitle() {
      return this.form.processCoopDeliveryNumber || "工序委外发货单";
    },
    detailTableData() {
      return [this.form];
    },
  },
  created() {
    this.initPage();
  },
  methods: {
    formatProcessOrderLabel,
    formatProcessOrderOptionDesc,
    formatSpecLabel,
    async initPage() {
      this.pageLoading = true;
      try {
        await this.loadOrderOptions();
        if (this.finish_type === 1 && this.itemList.length) {
          this.form = applyProcessDeliveryFields(
            this.form,
            this.itemList[0]
          );
          this.ensureOrderOption(this.form);
          return;
        }
        if (Number(this.routeType) === 1 && this.routeQuery) {
          await this.fillFromOrder(this.routeQuery);
        }
      } finally {
        this.pageLoading = false;
      }
    },
    ensureOrderOption(option) {
      const docId =
        option.baseProcessOutsourcingDocId || option.processOutsourcingDocId;
      if (
        !docId ||
        this.orderOptions.some(
          (item) =>
            (item.processOutsourcingDocId ||
              item.baseProcessOutsourcingDocId) === docId
        )
      ) {
        return;
      }
      this.orderOptions.unshift({
        ...option,
        processOutsourcingDocId: docId,
        baseProcessOutsourcingDocId: docId,
        processOutsourcingDocNumber:
          option.baseProcessOutsourcingDocNumber ||
          option.processOutsourcingDocNumber,
      });
    },
    async loadOrderOptions() {
      try {
        const res = await processOutsourcing_list({
          orderParam: [{ asc: false, fieldName: "createTime" }],
          processOutsourcingDtoFilter: {},
          pageParam: { pageNum: 1, pageSize: 500 },
        });
        this.orderOptions = res.data.data?.dataList || [];
      } catch (error) {
        this.orderOptions = [];
      }
    },
    async fillFromOrder(source) {
      if (!source) return;
      const { orderRow, preload } = normalizeProcessOrderSource(source);
      const fields = pickProcessOrderFields(orderRow);
      const orderQty = resolveProcessDeliveryQuantity(orderRow);
      // 先落订单字段与数量，避免后续预填把数量冲成 0
      this.form = applyProcessDeliveryFields(this.form, {
        ...fields,
        ...pickProcessMaterialFields(preload || {}),
        baseOutsourcingFinishedQuantity: resolveProcessDeliveryQuantity(
          preload || {},
          orderQty
        ),
      });
      this.ensureOrderOption({ ...orderRow, ...fields });
      const docId = fields.baseProcessOutsourcingDocId;
      if (!docId) return;
      // 订单页已带 preload 时不再重复请求，防止二次覆盖
      if (preload) return;
      try {
        const res =
          await processOutsourcingReceive_processOutsourcingCanReceiveProduction(
            { processOutsourcingDocId: docId }
          );
        const data = res.data.data;
        if (!data) return;
        const nextQty = resolveProcessDeliveryQuantity(
          data,
          this.form.baseOutsourcingFinishedQuantity
        );
        this.form = applyProcessDeliveryFields(this.form, {
          ...pickProcessMaterialFields(data),
          baseOutsourcingFinishedQuantity:
            nextQty > 0
              ? nextQty
              : this.form.baseOutsourcingFinishedQuantity,
        });
      } catch (error) {
        // 预填失败时保留订单数量
      }
    },
    async handleOrderChange(docId) {
      if (!docId) {
        this.form = createProcessDeliveryForm();
        return;
      }
      const order = this.orderOptions.find(
        (item) =>
          (item.processOutsourcingDocId ||
            item.baseProcessOutsourcingDocId) === docId
      );
      if (!order) return;
      const blockMsg = getProcessShipBlockMessage(order);
      if (blockMsg) {
        this.$message.warning(blockMsg);
        this.form = createProcessDeliveryForm();
        return;
      }
      await this.fillFromOrder(order);
    },
    showValidationError(options) {
      const result = validateProcessDeliveryForm(this.form, options);
      if (!result.valid) {
        this.$message.error(result.message);
      }
      return result.valid;
    },
    async handleSaveDraft() {
      if (!this.showValidationError({ requireQty: false })) {
        return;
      }
      if (this.docIdExists) {
        this.$message.info("草稿已保存，可直接提交");
        return;
      }
      this.draftLoading = true;
      try {
        const res = await processCoopDelivery_saveDraft(
          buildProcessDeliveryPayload(this.form)
        );
        this.form.processCoopDeliveryId = res.data.data;
        this.form.statusCooperativeDoc = PROCESS_COOP_DOC_STATUS.DRAFT;
        this.$message.success("草稿已保存");
      } finally {
        this.draftLoading = false;
      }
    },
    async handleSubmit() {
      if (!this.showValidationError({ requireQty: true })) {
        return;
      }
      this.submitLoading = true;
      try {
        let docId = this.form.processCoopDeliveryId;
        if (!this.docIdExists) {
          const res = await processCoopDelivery_add(
            buildProcessDeliveryPayload(this.form)
          );
          docId = res.data.data;
          this.form.processCoopDeliveryId = docId;
          this.form.statusCooperativeDoc = PROCESS_COOP_DOC_STATUS.NEW;
        } else if (this.isCooperativeDraft) {
          await processCoopDelivery_submitDraft(
            buildProcessDraftActionPayload(docId)
          );
          this.form.statusCooperativeDoc = PROCESS_COOP_DOC_STATUS.NEW;
        }
        this.$message.success("操作成功");
        this.emitSubmitSuccess(docId);
      } finally {
        this.submitLoading = false;
      }
    },
    async handleAudit(type) {
      const auditType = Number(type) === 2 ? 2 : 1;
      const globalId = resolveProcessAuditGlobalId(this.form);
      if (!globalId) {
        this.$message.error("缺少发货单 ID");
        return;
      }
      this.auditLoading = true;
      try {
        await processCoopDelivery_auditByGlobal(
          buildProcessAuditPayload(globalId, auditType)
        );
        this.$message.success(auditType === 1 ? "审核成功" : "已取消审核");
        this.emitSubmitSuccess(globalId);
      } finally {
        this.auditLoading = false;
      }
    },
    emitSubmitSuccess(docId) {
      if (this.finish_type === 1) {
        this.$emit("backOrderState");
        return;
      }
      this.$emit("backHome", 172, { processCoopDeliveryId: docId });
    },
    backHome() {
      this.$emit("backHome");
    },
  },
};
</script>

<style scoped lang="scss">
.delivery-page {
  min-height: calc(100vh - 132px);
  background: #fff;
}
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-header__title {
  margin: 0 0 6px;
  font-size: 22px;
  line-height: 1.3;
  color: #1476c3;
  font-weight: 600;
}
.page-header__actions {
  display: flex;
  gap: 8px;
  padding-top: 4px;
}
.delivery-card {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  margin-bottom: 16px;
  overflow: hidden;
}
.delivery-card__head {
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 12px 20px;
  background: linear-gradient(90deg, #eef6ff 0%, #f9fcff 100%);
  border-bottom: 1px solid #e4e7ed;
}
.delivery-card__head--plain {
  background: #fff;
}
.delivery-card__label {
  color: #606266;
  font-size: 14px;
  margin-right: 10px;
}
.delivery-card__title {
  color: #303133;
  font-size: 15px;
  font-weight: 600;
}
.delivery-form {
  padding: 20px 20px 4px;
}
.delivery-card--detail {
  padding-bottom: 16px;
}
.detail-table {
  margin: 16px 20px 0;
  width: calc(100% - 40px);
}
::v-deep .detail-qty-input {
  width: 100%;
  .el-input__inner {
    text-align: left;
  }
}
</style>

<style lang="scss">
.process-order-select-dropdown {
  .el-select-dropdown__item {
    height: auto;
    min-height: 34px;
    line-height: 1.4;
    padding-top: 8px;
    padding-bottom: 8px;
  }
}
.process-order-option__title {
  color: #303133;
  font-size: 13px;
  font-weight: 600;
}
.process-order-option__desc {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
  white-space: normal;
}
</style>
